import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { RouteComponentProps } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import TitleAndBack from 'components/v2/TitleAndBack';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import EpdRoutes from 'routes/EpdRoutes';
import { getDocumentVersion, getVersionLogSchema, getDocumentDataSources, getPropertiesSchema } from 'services/api/dynamicFormsApi';
import { QUERY_KEYS } from 'services/api/constants';
import { Container } from 'styles/Styles.styled';
import { WizardHeaderGrid } from 'styles/v2/Styles.styled';
import DynamicContentRenderer from './DynamicContentRenderer';
import { DataSources } from 'types/dynamic-forms/DataSources';
import { VersionLogContext } from './hooks/useVersionLogContext';
import { VersionLogRootRecord } from './records/VersionLogRootRecord';
import { DocumentProperty } from 'types/dynamic-forms/DocumentProperty';
import styled from 'styled-components';

interface Params {
  docId: string;
  versionId: string;
}

const DynamicFormVersionLog: React.FC<RouteComponentProps<Params>> = ({
  match: {
    params: { docId, versionId },
  },
  history,
}) => {
  const versionQuery = useQuery({
    queryKey: ['DOC_VERSION', docId, versionId],
    queryFn: () => getDocumentVersion(docId, versionId),
    throwOnError: true,
  });

  const schemaId = versionQuery.data?.schemaId;

  const versionLogQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'VERSION_LOG', schemaId],
    queryFn: () => getVersionLogSchema(schemaId!) as Promise<VersionLogRootRecord>,
    enabled: !!versionQuery.data,
    throwOnError: true,
  });

  const dataSourcesQuery = useQuery({
    queryKey: QUERY_KEYS.DYNAMIC_FORMS_DATA_SOURCES(docId, schemaId, versionId),
    queryFn: () => getDocumentDataSources(schemaId!, docId, versionId) as Promise<DataSources>,
    enabled: !!versionQuery.data,
    throwOnError: true,
  });

  const propertiesQuery = useQuery({
    queryKey: ['DOC_SCHEMA', 'PROPERTIES', schemaId],
    queryFn: () => getPropertiesSchema(schemaId!) as Promise<DocumentProperty[]>,
    enabled: !!versionQuery.data,
    throwOnError: true,
  });

  const isLoading =
    versionQuery.isLoading || versionLogQuery.isLoading || dataSourcesQuery.isLoading || propertiesQuery.isLoading;

  if (isLoading || !versionQuery.data || !versionLogQuery.data || !dataSourcesQuery.data || !propertiesQuery.data) {
    return (
      <Container>
        <Skeleton width="100%" height="4rem" className="mb-2" />
        <Skeleton width="100%" height="12rem" className="mb-2" />
        <Skeleton width="100%" height="12rem" className="mb-2" />
        <Skeleton width="100%" height="12rem" />
      </Container>
    );
  }

  const { data, metadata } = versionQuery.data;

  return (
    <PageWrapper>
      <WizardHeaderGrid>
        <TitleAndBack
          title={metadata.fullIdentificationNumber ?? metadata.title ?? ''}
          backTo="Back to the EPD"
          onBack={() => history.push(EpdRoutes.param(EpdRoutes.dynamicForm, ':docId', docId))}
        />
      </WizardHeaderGrid>

      <VersionLogContext.Provider
        value={{ data, metadata, dataSources: dataSourcesQuery.data, properties: propertiesQuery.data }}>
        <DynamicContentRenderer schema={versionLogQuery.data} />
      </VersionLogContext.Provider>
    </PageWrapper>
  );
};

export default withGreyBackground(DynamicFormVersionLog);

const PageWrapper = styled(Container)`
  .p-datatable-tbody > tr > td,
  .p-datatable-tfoot > tr > td {
    font-size: 14px;
    line-height: 16px;
  }
`;